import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SelectPlan from "@/pages/SelectPlan";
import Payment from "@/pages/Payment";
import SubscriptionStatus from "@/pages/SubscriptionStatus";
import Dashboard from "@/pages/Dashboard";
import ExamList from "@/pages/ExamList";
import ExamTake from "@/pages/ExamTake";
import ExamResult from "@/pages/ExamResult";
import ExamHistory from "@/pages/ExamHistory";
import ClientList from "@/pages/ClientList";
import ClientDetail from "@/pages/ClientDetail";
import ClientNew from "@/pages/ClientNew";
import StudyMaterials from "@/pages/StudyMaterials";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminExams from "@/pages/admin/AdminExams";
import AdminExamEdit from "@/pages/admin/AdminExamEdit";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminMaterials from "@/pages/admin/AdminMaterials";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import MyAccount from "@/pages/MyAccount";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireActive = true }) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground font-mono text-sm uppercase tracking-widest">
          {t("loading")}
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (requireActive && !user.is_active && user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h1 className="font-serif text-3xl mb-4">{t("pending_account")}</h1>
          <p className="text-muted-foreground mb-6">
            {t("pending_account_message")}
          </p>
          <button 
            onClick={() => window.location.href = "/"}
            className="btn-secondary"
          >
            {t("back_to_home")}
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground font-mono text-sm uppercase tracking-widest">
          {t("loading")}
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<Register />} />
      
      {/* Subscription Routes (authenticated but no active requirement) */}
      <Route path="/select-plan" element={<ProtectedRoute requireActive={false}><SelectPlan /></ProtectedRoute>} />
      <Route path="/payment" element={<ProtectedRoute requireActive={false}><Payment /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute requireActive={false}><SubscriptionStatus /></ProtectedRoute>} />
      <Route path="/my-account" element={<ProtectedRoute requireActive={false}><MyAccount /></ProtectedRoute>} />
      
      {/* Student Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/exams" element={<ProtectedRoute><ExamList /></ProtectedRoute>} />
      <Route path="/exams/:examId" element={<ProtectedRoute><ExamTake /></ProtectedRoute>} />
      <Route path="/exams/:examId/result/:attemptId" element={<ProtectedRoute><ExamResult /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><ExamHistory /></ProtectedRoute>} />
      <Route path="/materials" element={<ProtectedRoute><StudyMaterials /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientList /></ProtectedRoute>} />
      <Route path="/clients/new" element={<ProtectedRoute><ClientNew /></ProtectedRoute>} />
      <Route path="/clients/:clientId" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/subscriptions" element={<ProtectedRoute requireAdmin><AdminSubscriptions /></ProtectedRoute>} />
      <Route path="/admin/exams" element={<ProtectedRoute requireAdmin><AdminExams /></ProtectedRoute>} />
      <Route path="/admin/exams/new" element={<ProtectedRoute requireAdmin><AdminExamEdit /></ProtectedRoute>} />
      <Route path="/admin/exams/:examId" element={<ProtectedRoute requireAdmin><AdminExamEdit /></ProtectedRoute>} />
      <Route path="/admin/templates" element={<ProtectedRoute requireAdmin><AdminTemplates /></ProtectedRoute>} />
      <Route path="/admin/materials" element={<ProtectedRoute requireAdmin><AdminMaterials /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
